/**
 * Las fotos viajan aparte.
 *
 * Events carry a photo's name; this moves its bytes. It runs after a sync has finished rather
 * than inside one, and nothing waits on it: a sale must never sit in the outbox behind a
 * picture on a slow connection, and a photo that fails to move costs a product its picture on
 * another phone for a while — never a record.
 *
 * Up: every photo this device took that a product or a client's cédula is wearing and the
 * server does not have yet. A photo taken for something that was then never saved goes nowhere,
 * and after a day it is let go — a cédula nobody kept has no business living on in a phone.
 *
 * Down: every photo a product here is wearing that this device does not hold — and any other
 * photo a screen here has asked for, which is how a client's cédula reaches the owner's phone
 * when the owner opens it, and no other phone. Fetched once and kept — the name is the content,
 * so a copy is never stale and there is never a reason to ask twice. A photo the server does
 * not have yet is ordinary (the phone that took it may still be offline), so it is asked for
 * again later, not every time a list is drawn.
 */

import { huellaFoto, tipoDeImagen } from '@abastece/shared';
import { db, getMeta } from '../db/dexie.js';
import { enPractica } from '../lib/practica.js';
import { ApiError, OfflineError, api } from './client.js';

/** Enough to fill a screen of thumbnails quickly without flooding one small server. */
const CONCURRENCIA = 3;

/** How long a photo the server did not have is left alone before it is asked for again. */
const ESPERA_SI_FALTA_MS = 5 * 60_000;

/**
 * A cédula the server would not give this device: it goes only where the owner is signed in, as
 * the server knows it. The owner's sign-in reaching the server is what changes the answer, so it
 * is asked again soon.
 */
const ESPERA_SI_NO_DEJA_MS = 30_000;

/** huella → the moment it may be asked for again. Per page load, which is often enough. */
const enEspera = new Map<string, number>();

/** Photos a screen here asked for that no product wears: a client's cédula, opened by the owner. */
const pedidas = new Set<string>();

/** How long a photo nothing is wearing is kept: the form it was taken for may still be open. */
const OLVIDAR_TRAS_MS = 24 * 60 * 60_000;

/**
 * Refusals that will not change on a retry: the bytes themselves are wrong. Anything else — no
 * signal, a server error, a token being re-issued — is worth trying again next time.
 */
const esDefinitivo = (status: number) => status === 400 || status === 413 || status === 415 || status === 422;

async function subir(): Promise<void> {
  const pendientes = await db.fotos.where('estado').equals('pendiente').toArray();
  if (pendientes.length === 0) return;

  const enUso = new Set(
    [
      ...(await db.productos.toArray()).map((p) => p.foto),
      ...(await db.clientes.toArray()).map((c) => c.fotoCedula),
      ...(await db.gastos.toArray()).map((g) => g.foto),
    ].filter(Boolean),
  );
  for (const f of pendientes) {
    if (!enUso.has(f.huella)) {
      if (Date.now() - Date.parse(f.creadaEn) > OLVIDAR_TRAS_MS) await db.fotos.delete(f.huella);
      continue;
    }
    try {
      await api.subirFoto(f.huella, f.datos);
      await db.fotos.update(f.huella, { estado: 'subida' });
    } catch (e) {
      if (e instanceof ApiError && esDefinitivo(e.status)) {
        // Kept, so this device can still show it; no longer retried, so it does not cost a
        // request every thirty seconds for the rest of the phone's life.
        console.warn('[fotos] el servidor no aceptó la foto', f.huella, e.message);
        await db.fotos.update(f.huella, { estado: 'rechazada' });
        continue;
      }
      throw e;
    }
  }
}

async function bajarUna(huella: string): Promise<void> {
  let blob: Blob;
  try {
    blob = await api.bajarFoto(huella);
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
      enEspera.set(huella, Date.now() + (e.status === 403 ? ESPERA_SI_NO_DEJA_MS : ESPERA_SI_FALTA_MS));
      return;
    }
    throw e;
  }

  const bytes = new Uint8Array(await blob.arrayBuffer());
  const tipo = tipoDeImagen(bytes);
  /**
   * Checked here as well as on the server. The name is a promise about the bytes, and a
   * damaged download kept under it would be drawn on this phone for as long as it lives.
   */
  if (!tipo || (await huellaFoto(bytes)) !== huella) {
    enEspera.set(huella, Date.now() + ESPERA_SI_FALTA_MS);
    return;
  }

  await db.transaction('rw', db.fotos, async () => {
    if (await db.fotos.get(huella)) return;
    await db.fotos.put({
      huella,
      tipo,
      datos: new Blob([bytes], { type: tipo }),
      estado: 'bajada',
      creadaEn: new Date().toISOString(),
    });
  });
  enEspera.delete(huella);
  pedidas.delete(huella);
}

async function bajar(): Promise<void> {
  const quiere = new Set<string>(pedidas);
  // What is on sale, ahead of anybody asking. A product taken off sale shows its photo when its
  // page asks for it (`pedirFoto`), and until then costs no phone its data.
  for (const p of await db.productos.toArray()) if (p.foto && p.activo) quiere.add(p.foto);
  if (quiere.size === 0) return;

  const tiene = new Set(await db.fotos.toCollection().primaryKeys());
  const ahora = Date.now();
  const faltan = [...quiere].filter((h) => !tiene.has(h) && (enEspera.get(h) ?? 0) <= ahora);

  let siguiente = 0;
  const trabajador = async () => {
    while (siguiente < faltan.length) await bajarUna(faltan[siguiente++]!);
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCIA, faltan.length) }, trabajador));
}

let enVuelo: Promise<void> | null = null;
let otraVez = false;

/**
 * Coalesced like the sync itself: one pass at a time, and a request that arrives during a
 * pass buys exactly one more pass rather than a queue of them.
 */
export function transferirFotos(): Promise<void> {
  // A practice shop never talks to the server, photos included.
  if (enPractica()) return Promise.resolve();
  if (enVuelo) {
    otraVez = true;
    return enVuelo;
  }
  enVuelo = (async () => {
    try {
      if (!(await getMeta('token'))) return;
      do {
        otraVez = false;
        await subir();
        await bajar();
      } while (otraVez);
    } catch (e) {
      // No signal is the normal state of a phone in a shop, not something to report.
      if (!(e instanceof OfflineError)) console.warn('[fotos]', e);
    } finally {
      enVuelo = null;
    }
  })();
  return enVuelo;
}

let programada: ReturnType<typeof setTimeout> | undefined;

/**
 * A thumbnail asking for a photo this device does not hold.
 *
 * A list of forty products calls this forty times in one render; the short delay folds them
 * into one pass. A photo already known to be missing on the server is not asked about again
 * until its wait is up.
 */
export function pedirFoto(huella: string): void {
  if (enPractica()) return;
  pedidas.add(huella);
  if ((enEspera.get(huella) ?? 0) > Date.now()) return;
  if (programada !== undefined) return;
  programada = setTimeout(() => {
    programada = undefined;
    void transferirFotos();
  }, 400);
}
